/**
 * Paliacate Studio - Editorial Homepage
 * Style inspired by forthepeople.agency
 * 
 * Design principles:
 * - Movement is narrative, not decoration
 * - Scroll is the director - sections reveal progressively
 * - Custom cursor with contextual behavior
 * - Nothing appears suddenly, everything emerges
 * - Alternating black/white sections for visual rhythm
 * 
 * SEO: H1 keyword, heading hierarchy, 400+ words indexable, alt tags, internal links
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageWrapper } from './EditorialShared';

// Portfolio images for hero hover effect - trail style
const portfolioImages = [
  { src: "https://drive.google.com/thumbnail?id=15aukMIPeeTfvtBI1U10fCgSgB9l5HK2H&sz=w400", alt: "Branding para Café Fantasía — cafetería artesanal en Cuernavaca" },
  { src: "https://drive.google.com/thumbnail?id=1lv0noT1zD56g7FEtuEI_ZT2XcrgO6yoP&sz=w400", alt: "Identidad visual de Altitud Pádel — club deportivo premium" },
  { src: "https://drive.google.com/thumbnail?id=1oCpgmt7rXyl4yFboo8GrmZyox3jgbPK-&sz=w400", alt: "Diseño de marca El Exquisito — cocina mixe tradicional" },
  { src: "https://drive.google.com/thumbnail?id=1qDGeNYMWsXTpAARN_D0sH7tJPwQS5ddx&sz=w400", alt: "Branding Torii Sushi — restaurante japonés contemporáneo" },
  { src: "https://drive.google.com/thumbnail?id=1zbthWuHnoAle7l4o69uzNGoa7vnikhRq&sz=w400", alt: "Identidad visual Comic Cafe 2D — concepto creativo" },
  { src: "https://drive.google.com/thumbnail?id=1WajCZ48vXJCj9RjhfoF0CYBwEs6YlHt7&sz=w400", alt: "Diseño de marca para cocktail bar en Morelos" },
  { src: "https://drive.google.com/thumbnail?id=1XXpbb0yGE5vGapKbg05lewXLiPhfseoL&sz=w400", alt: "Branding Andrea Gamoneda — marca personal de estilo de vida" },
  { src: "https://drive.google.com/thumbnail?id=1Jgm36umwa0oBSExa8cAsZjKVGli76GaU&sz=w400", alt: "Identidad visual The Clutch — marca de moda y accesorios" },
];

// Single trailing image that fades out
const TrailingImage = ({ image, position, rotation, id, onRemove }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(id);
    }, 1500);
    return () => clearTimeout(timer);
  }, [id, onRemove]);

  return (
    <motion.div
      className="fixed pointer-events-none z-40"
      initial={{ opacity: 0, scale: 0.3, rotate: rotation - 10 }}
      animate={{ opacity: 1, scale: 1, rotate: rotation }}
      exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.5 } }}
      transition={{ 
        opacity: { duration: 0.2 },
        scale: { duration: 0.3, type: "spring", stiffness: 300, damping: 20 },
        rotate: { duration: 0.3 }
      }}
      style={{ 
        left: position.x - 90, 
        top: position.y - 60,
        width: 180, 
        height: 120 
      }}
    >
      <img
        src={image.src}
        alt={image.alt}
        className="w-full h-full object-cover shadow-2xl"
        loading="lazy"
      />
    </motion.div>
  );
};

// Hero section with image trail effect
const HeroSection = () => {
  const ref = useRef(null);
  const [trailingImages, setTrailingImages] = useState([]);
  const [imageIndex, setImageIndex] = useState(0);
  const lastPosition = useRef({ x: 0, y: 0 });
  const isHoveringHero = useRef(false);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, -100]);

  const removeImage = useCallback((id) => {
    setTrailingImages(prev => prev.filter(img => img.id !== id));
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isHoveringHero.current) return;
    
    const distance = Math.sqrt(
      Math.pow(e.clientX - lastPosition.current.x, 2) +
      Math.pow(e.clientY - lastPosition.current.y, 2)
    );
    
    if (distance > 80) {
      lastPosition.current = { x: e.clientX, y: e.clientY };
      
      const newImage = {
        id: Date.now() + Math.random(),
        image: portfolioImages[imageIndex % portfolioImages.length],
        position: { x: e.clientX, y: e.clientY },
        rotation: (Math.random() - 0.5) * 20,
      };
      
      setTrailingImages(prev => [...prev.slice(-12), newImage]);
      setImageIndex(prev => prev + 1);
    }
  }, [imageIndex]);

  const handleMouseEnter = () => {
    isHoveringHero.current = true;
  };

  const handleMouseLeave = () => {
    isHoveringHero.current = false;
  };

  return (
    <section 
      id="inicio"
      ref={ref} 
      className="min-h-screen flex flex-col justify-center items-center px-6 relative overflow-hidden bg-black"
      onMouseMove={handleMouseMove}
    >
      {/* Trailing images */}
      <AnimatePresence>
        {trailingImages.map((item) => (
          <TrailingImage
            key={item.id}
            id={item.id}
            image={item.image}
            position={item.position}
            rotation={item.rotation}
            onRemove={removeImage}
          />
        ))}
      </AnimatePresence>

      <motion.div 
        className="text-center max-w-6xl relative z-10"
        style={{ opacity, y }}
      >
        <motion.div
          className="text-white text-[14vw] md:text-[12vw] lg:text-[10vw] leading-[0.9] tracking-tight cursor-pointer select-none"
          style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.25, 0.1, 0, 1] }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-cursor="explorar"
        >
          <span className="block">Let's</span>
          <span className="block italic">Paliacate</span>
          <span className="block">it.</span>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 text-white/30 text-xs uppercase tracking-[0.3em]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2"
        >
          <span>Scroll</span>
          <motion.div 
            className="w-px h-8 bg-white/20"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ delay: 2.5, duration: 0.8 }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

// About section — WHITE BACKGROUND — brief intro with keywords
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-15%" });

  return (
    <section id="nosotros" className="bg-white py-24 md:py-32 px-6" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.p
          className="text-black/40 text-sm uppercase tracking-[0.2em] mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          Quiénes somos
        </motion.p>

        {/* SEO H1 — keyword heading, visually integrated in first content section */}
        <motion.h1
          className="text-black text-2xl md:text-4xl lg:text-5xl leading-[1.15] mb-8"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
          data-testid="seo-h1"
        >
          Agencia de Marketing Digital y Branding en Cuernavaca
        </motion.h1>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="text-black/60 text-base md:text-lg leading-relaxed">
            Somos una agencia de marketing digital en Cuernavaca especializada en construir marcas que importan. 
            En Paliacate Studio creemos que cada marca tiene una historia que merece ser contada con autenticidad. 
            Desde 2021, hemos trabajado con más de 230 clientes en Cuernavaca, Morelos y toda la República Mexicana, 
            creando identidades visuales, estrategias de redes sociales y producción audiovisual de alto nivel.
          </p>
          <p className="text-black/60 text-base md:text-lg leading-relaxed">
            Nos especializamos en negocios de gastronomía, hospitalidad y estilo de vida que buscan destacar 
            con una imagen profesional y coherente. No creamos plantillas genéricas — diseñamos sistemas de marca 
            completos que transforman la percepción de tu negocio y atraen a los clientes correctos.
          </p>
        </motion.div>

        {/* Internal navigation links */}
        <motion.nav
          className="flex flex-wrap gap-4 md:gap-8 mt-12 pt-8 border-t border-black/10"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          aria-label="Secciones principales"
        >
          <a href="#servicios" className="text-black/50 hover:text-black text-sm uppercase tracking-wider transition-colors">Servicios</a>
          <a href="#portafolio" className="text-black/50 hover:text-black text-sm uppercase tracking-wider transition-colors">Portafolio</a>
          <Link to="/metodo" className="text-black/50 hover:text-black text-sm uppercase tracking-wider transition-colors">Método</Link>
          <Link to="/equipo" className="text-black/50 hover:text-black text-sm uppercase tracking-wider transition-colors">Equipo</Link>
          <a href="#contacto" className="text-black/50 hover:text-black text-sm uppercase tracking-wider transition-colors">Contacto</a>
        </motion.nav>
      </div>
    </section>
  );
};

// Question section - WHITE BACKGROUND (contrast section)
const QuestionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-20%" });

  return (
    <section className="bg-black py-32 md:py-40 px-6" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.p
          className="text-white/40 text-sm uppercase tracking-[0.2em] mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          La pregunta
        </motion.p>
        
        <motion.h2
          className="text-white text-3xl md:text-5xl lg:text-6xl font-extralight leading-[1.1] mb-12"
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.1, 0, 1] }}
        >
          ¿Por qué todas las marcas se ven iguales? ¿Por qué la mayoría del contenido visual parece hecho con la misma plantilla?
        </motion.h2>
        
        <motion.div
          className="flex flex-col md:flex-row gap-6 md:gap-12"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <Link 
            to="/manifiesto" 
            className="text-white text-lg md:text-xl hover:opacity-50 transition-opacity underline underline-offset-8 decoration-white/30"
            data-cursor="descubrir"
          >
            ¿Qué hace una mejor marca?
          </Link>
          <Link 
            to="/metodo" 
            className="text-white text-lg md:text-xl hover:opacity-50 transition-opacity underline underline-offset-8 decoration-white/30"
            data-cursor="conocer"
          >
            ¿Cómo las construimos?
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Services section — WHITE BACKGROUND — keyword-rich service descriptions
const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-15%" });

  const services = [
    {
      title: "Gestión de Redes Sociales",
      description: "Administramos tu presencia en Facebook, Instagram, TikTok y Google Maps con contenido visual profesional, calendario editorial y reportes de resultados mensuales."
    },
    {
      title: "Branding e Identidad Visual",
      description: "Diseño de logotipo, brandbook y sistema visual completo. Creamos la identidad de marca que diferencia a tu negocio en Cuernavaca y más allá."
    },
    {
      title: "Fotografía y Video Profesional",
      description: "Sesiones fotográficas comerciales y producción audiovisual. Más de 17,000 fotografías profesionales para marcas de gastronomía, hospitalidad y retail."
    },
    {
      title: "Diseño y Desarrollo Web",
      description: "Sitios web profesionales, landing pages y tiendas virtuales optimizados para convertir visitantes en clientes."
    },
    {
      title: "Campañas Publicitarias Digitales",
      description: "Estrategia y gestión de campañas en Google Ads, Facebook Ads y YouTube para maximizar tu inversión publicitaria."
    }
  ];

  return (
    <section id="servicios" className="bg-white py-24 md:py-32 px-6" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.p
          className="text-black/40 text-sm uppercase tracking-[0.2em] mb-4"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
        >
          Nuestros servicios
        </motion.p>

        <motion.h2
          className="text-black text-2xl md:text-4xl font-extralight leading-[1.15] mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          Servicios de marketing digital en Cuernavaca — todo lo que tu marca necesita para crecer.
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
            >
              <h3 className="text-black text-lg md:text-xl font-medium mb-2">{service.title}</h3>
              <p className="text-black/55 text-base leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-14"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <Link
            to="/servicios"
            className="inline-block text-black text-base border border-black/20 px-8 py-4 hover:bg-black hover:text-white transition-all duration-300"
            data-cursor="ver más"
          >
            Ver todos los servicios
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Portfolio projects data
const portfolioProjects = [
  { 
    id: 1, 
    name: "Café Fantasía", 
    description: "Universo visual para una cafetería artesanal.",
    image: "https://drive.google.com/thumbnail?id=15aukMIPeeTfvtBI1U10fCgSgB9l5HK2H&sz=w1200",
    alt: "Branding para cafetería artesanal en Cuernavaca — agencia de marketing digital Paliacate Studio",
    year: "2024"
  },
  { 
    id: 2, 
    name: "Altitud Pádel", 
    description: "Identidad con energía y presencia premium.",
    image: "https://drive.google.com/thumbnail?id=1lv0noT1zD56g7FEtuEI_ZT2XcrgO6yoP&sz=w1200",
    alt: "Identidad visual para club de pádel — branding deportivo profesional por Paliacate Studio",
    year: "2024"
  },
  { 
    id: 3, 
    name: "El Exquisito", 
    description: "La esencia de la cocina mixe tradicional.",
    image: "https://drive.google.com/thumbnail?id=1oCpgmt7rXyl4yFboo8GrmZyox3jgbPK-&sz=w1200",
    alt: "Diseño de marca para restaurante de cocina mexicana — fotografía comercial Morelos",
    year: "2023"
  },
  { 
    id: 4, 
    name: "Torii Sushi", 
    description: "Tradición japonesa con creatividad contemporánea.",
    image: "https://drive.google.com/thumbnail?id=1qDGeNYMWsXTpAARN_D0sH7tJPwQS5ddx&sz=w1200",
    alt: "Branding para restaurante japonés — identidad visual y fotografía por agencia de marketing Cuernavaca",
    year: "2023"
  },
];

// Project card with staggered reveal
const ProjectCard = ({ project, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 100 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, delay: index * 0.2, ease: [0.25, 0.1, 0, 1] }}
    >
      <Link 
        to="/casos" 
        className="group block"
        data-cursor="ver proyecto"
      >
        <div className="aspect-[4/3] overflow-hidden mb-6">
          <motion.img 
            src={project.image} 
            alt={project.alt}
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            animate={isInView ? { scale: 1 } : {}}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 1.2 }}
            loading="lazy"
          />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 + index * 0.2, duration: 0.6 }}
        >
          <div className="flex justify-between items-baseline mb-2">
            <h3 className="text-white text-xl md:text-2xl font-medium">{project.name}</h3>
            <span className="text-white/30 text-sm">{project.year}</span>
          </div>
          <p className="text-white/50">{project.description}</p>
        </motion.div>
      </Link>
    </motion.div>
  );
};

// Portfolio section - BLACK BACKGROUND
const PortfolioSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <section id="portafolio" className="bg-black py-24 md:py-32 px-6" ref={ref}>
      <div className="max-w-6xl mx-auto">
        <motion.p
          className="text-white/40 text-sm uppercase tracking-[0.2em] mb-4"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
        >
          Nuestro trabajo
        </motion.p>
        
        <motion.h2
          className="text-white/60 text-xl md:text-2xl font-light mb-4"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Algunos ejemplos de cómo hemos puesto esto en práctica:
        </motion.h2>
        
        <motion.div 
          className="text-white text-3xl mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
        >
          ↓
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
          {portfolioProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>

        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <Link 
            to="/casos" 
            className="inline-block text-white text-lg border border-white/20 px-8 py-4 hover:bg-white hover:text-black transition-all duration-300"
            data-cursor="ver más"
          >
            Ver todos los proyectos
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Stats section - WHITE BACKGROUND (contrast)
const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-20%" });

  const stats = [
    { number: "+500", label: "Proyectos entregados" },
    { number: "+17,000", label: "Fotografías profesionales" },
    { number: "+230", label: "Clientes satisfechos" },
    { number: "5+", label: "Años de experiencia" },
  ];

  return (
    <section className="bg-white py-32 md:py-40 px-6" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.p
          className="text-black/40 text-sm uppercase tracking-[0.2em] mb-4 text-center"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
        >
          En números
        </motion.p>

        <motion.h2
          className="text-black text-xl md:text-2xl font-extralight text-center mb-16 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          5+ años transformando marcas en Morelos y toda la República Mexicana
        </motion.h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.15, duration: 0.8, ease: [0.25, 0.1, 0, 1] }}
            >
              <motion.div 
                className="text-black text-5xl md:text-7xl font-extralight"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.3 + i * 0.15, duration: 0.6 }}
              >
                {stat.number}
              </motion.div>
              <div className="text-black/40 text-sm mt-3 uppercase tracking-wider">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Trust signals section — BLACK BACKGROUND — Testimonials & client trust
const TrustSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-15%" });

  const testimonials = [
    {
      quote: "Paliacate transformó por completo la imagen de nuestro restaurante. Desde el branding hasta las redes sociales, todo se siente profesional y auténtico.",
      name: "Carlos M.",
      business: "Restaurante en Cuernavaca"
    },
    {
      quote: "La mejor inversión que hemos hecho. Nuestras redes sociales pasaron de tener contenido improvisado a una presencia que realmente representa quiénes somos.",
      name: "Ana L.",
      business: "Hotel boutique, Morelos"
    },
    {
      quote: "Son mucho más que una agencia de marketing. Entienden tu negocio, tu mercado y crean una estrategia que realmente funciona. Llevamos 3 años trabajando juntos.",
      name: "Roberto S.",
      business: "Cadena de cafeterías"
    }
  ];

  return (
    <section className="bg-black py-24 md:py-32 px-6" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.p
          className="text-white/40 text-sm uppercase tracking-[0.2em] mb-4"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
        >
          Lo que dicen nuestros clientes
        </motion.p>

        <motion.h2
          className="text-white/60 text-xl md:text-2xl font-light mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          Más de 230 clientes confían en nuestro trabajo.
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {testimonials.map((t, i) => (
            <motion.blockquote
              key={i}
              className="border-l border-white/15 pl-6"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.15 }}
            >
              <p className="text-white/70 text-base leading-relaxed mb-4">"{t.quote}"</p>
              <footer>
                <cite className="not-italic">
                  <span className="text-white text-sm font-medium block">{t.name}</span>
                  <span className="text-white/40 text-sm">{t.business}</span>
                </cite>
              </footer>
            </motion.blockquote>
          ))}
        </div>

        {/* Why choose us — E-E-A-T signal */}
        <motion.div
          className="mt-20 pt-16 border-t border-white/10"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <h3 className="text-white text-xl md:text-2xl font-light mb-8">
            ¿Por qué elegir Paliacate Studio como tu agencia de marketing en Cuernavaca?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-white/55 text-base leading-relaxed">
            <p>
              Conocemos el mercado local. Entendemos cómo se mueve el consumidor en Morelos y diseñamos estrategias 
              que conectan con tu público objetivo de forma real.
            </p>
            <p>
              No subcontratamos. Todo el proceso creativo — estrategia, diseño, fotografía, video y gestión de redes — 
              se realiza por nuestro equipo interno de 8 profesionales.
            </p>
            <p>
              Resultados medibles. Cada cliente recibe reportes mensuales con métricas claras de crecimiento, alcance 
              e interacción en sus plataformas digitales.
            </p>
          </div>
          <div className="flex flex-wrap gap-6 mt-8">
            <a href="https://www.instagram.com/paliacatestudio/" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white text-sm transition-colors">Instagram</a>
            <a href="https://www.facebook.com/paliacatestudio/" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white text-sm transition-colors">Facebook</a>
            <a href="https://www.tiktok.com/@paliacatestudio" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white text-sm transition-colors">TikTok</a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section - BLACK BACKGROUND
const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-20%" });

  return (
    <section id="contacto" className="bg-white py-32 md:py-40 px-6" ref={ref}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.h2
          className="text-black text-4xl md:text-6xl lg:text-7xl font-extralight leading-[1.1] mb-8"
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: [0.25, 0.1, 0, 1] }}
        >
          ¿Tienes un proyecto
          <br />
          en mente?
        </motion.h2>

        <motion.p
          className="text-black/50 text-lg md:text-xl mb-12 max-w-xl mx-auto"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          Cada marca es única. Platiquemos sobre lo que necesitas y cómo podemos ayudarte a crecer.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <a
            href="https://wa.me/527774385617"
            target="_blank"
            rel="noopener noreferrer"
            className="px-10 py-5 bg-black text-white font-medium hover:bg-black/80 transition-colors"
            data-cursor="escribir"
          >
            Hablemos por WhatsApp
          </a>
          <Link
            to="/contacto"
            className="px-10 py-5 border border-black/30 text-black hover:bg-black/5 transition-colors"
            data-cursor="contacto"
          >
            Más opciones de contacto
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Loading screen
const LoadingScreen = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 1500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div className="text-center">
        <motion.h1
          className="text-white text-4xl md:text-6xl font-extralight tracking-wider"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.span
            className="inline-block"
            animate={{ 
              opacity: [0, 1, 1, 0],
              y: [20, 0, 0, -20]
            }}
            transition={{ duration: 1.5, times: [0, 0.2, 0.8, 1] }}
          >
            Paliacate
          </motion.span>
        </motion.h1>
        
        {/* Loading bar */}
        <motion.div 
          className="mt-8 w-48 h-px bg-white/10 mx-auto overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <motion.div
            className="h-full bg-white"
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Main component
export const EditorialHomepage = () => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <>
      <AnimatePresence mode="wait">
        {isLoading && <LoadingScreen onComplete={() => setIsLoading(false)} />}
      </AnimatePresence>

      {!isLoading && (
        <PageWrapper showLogoFooter={true}>
          <HeroSection />
          <AboutSection />
          <QuestionSection />
          <ServicesSection />
          <PortfolioSection />
          <StatsSection />
          <TrustSection />
          <CTASection />
        </PageWrapper>
      )}
    </>
  );
};

export default EditorialHomepage;
